import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import moment from "moment";
import { formatStartDateIso, formatEndDateIso } from "../../../utils/dateUtils";

const KPICreateWork = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { taskTypes, eligibleUsers, loading } = useSelector((state) => state.kpi);
  const userInfoData = useSelector((state) => state.auth.userInfoData);
  const userProjects = useSelector((state) => state.projects.userProjects);

  const [formData, setFormData] = useState({
    task_name: "",
    task_description: "",
    task_priority: "Medium",
    task_due_date: "",
    assigned_kpi_points: "",
    task_kpi_type_id: "",
    project_id: "",
    assigned_to: []
  });

  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState("WEEKLY");
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    dispatch.kpi.getTaskTypes();
    dispatch.kpi.setEligibleUsers([]);
    if (userInfoData?._id) {
      dispatch.projects.allProjectsOfUser({ user_id: userInfoData._id });
    }
  }, [dispatch, userInfoData?._id]);

  useEffect(() => {
    if (formData.project_id) {
      dispatch.kpi.getEligibleUsers({ project_id: formData.project_id, limit: 100 });
    } else {
      dispatch.kpi.setEligibleUsers([]);
    }
  }, [dispatch, formData.project_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === "task_kpi_type_id") {
        const selected = displayTypes.find(t => t._id === value);
        if (selected) {
          updated.assigned_kpi_points = selected.score !== undefined ? selected.score : "";
        }
      }
      if (name === "project_id" && prev.project_id !== value) {
        updated.assigned_to = [];
      }
      return updated;
    });
  };

  const handleAssigneeChange = (selectedOption) => {
    setFormData(prev => ({ 
      ...prev, 
      assigned_to: selectedOption ? [selectedOption.value] : [] 
    }));
  };

  const handlePriority = (level) => {
    setFormData(prev => ({ ...prev, task_priority: level }));
  };

  const displayTypes = taskTypes || [];
  const projectsList = userProjects?.projects || [];
  const selectedType = displayTypes.find(t => t._id === formData.task_kpi_type_id);
  const isTypeRecurring = isRecurring || Boolean(selectedType?.name?.toLowerCase().includes("recurring"));
  const todayStr = moment().format("YYYY-MM-DD");

  const errors = {
    task_name: showErrors && (!formData.task_name || !formData.task_name.trim()),
    task_kpi_type_id: showErrors && !formData.task_kpi_type_id,
    project_id: showErrors && !formData.project_id,
    task_due_date: showErrors && (!isTypeRecurring && !formData.task_due_date),
    assigned_to: showErrors && (!formData.assigned_to || formData.assigned_to.length === 0),
    due_past: showErrors && (!isTypeRecurring && formData.task_due_date && formData.task_due_date < todayStr),
  };

  const handleSubmit = async () => {
    setShowErrors(true);
    if (!formData.task_name || !formData.task_name.trim() || !formData.task_kpi_type_id || !formData.project_id || (!isTypeRecurring && !formData.task_due_date) || !formData.assigned_to || formData.assigned_to.length === 0) {
      return;
    }
    try {
      const payload = {
        project_id: formData.project_id || null,
        task_name: formData.task_name,
        task_description: formData.task_description,
        task_priority: formData.task_priority,
        task_start_date: formatStartDateIso(todayStr),
        task_end_date: formatEndDateIso(formData.task_due_date),
        assigned_kpi_points: formData.assigned_kpi_points ? Number(formData.assigned_kpi_points) : null,
        task_kpi_type_id: formData.task_kpi_type_id || (displayTypes.length > 0 ? displayTypes[0]._id : null),
        assigned_by: userInfoData?._id || null,
        assigned_to: formData.assigned_to,
        ...(isRecurring ? {
          recurrence: {
            enabled: true,
            frequency: recurrenceFrequency,
            start_date: todayStr
          }
        } : {})
      };
      const res = await dispatch.kpi.createWork(payload);
      dispatch.kpi.getMyWorks({ status: "all" });
      dispatch.kpi.getKPIProjects({ hideLoading: true });

      const isRecurringResult = 
        isRecurring || 
        !!res?.recurrence || 
        !!res?.data?.recurrence || 
        (res?.message && res.message.toLowerCase().includes("recurring")) ||
        (res?.data?.message && res.data.message.toLowerCase().includes("recurring"));

      if (isRecurringResult) {
        navigate("/kpi/recurring");
      } else {
        navigate("/kpi/works");
      }
    } catch (err) {
      console.error("Failed to create work", err);
    }
  };

  return (
    <div className="w-full">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-4">
        <Link to="/kpi/works" className="hover:text-slate-900 transition-colors">Works</Link>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
        <span className="text-slate-900">Create work</span>
      </div>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Create new work</h1>
        <p className="text-slate-500 text-sm">
          Log a leadership activity and earn KPI points on completion
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Main Form Box */}
        <div className="flex-1 bg-white rounded-2xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-6">
          {/* 1. Work Title */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Work title <span className="text-rose-500">*</span></label>
            <input 
              type="text" 
              name="task_name"
              placeholder="e.g. Conduct weekly team sync and review OKRs..."
              value={formData.task_name}
              onChange={handleChange}
              className={`w-full px-3.5 py-2.5 border rounded-xl focus:outline-none focus:ring-2 transition-all text-sm text-slate-900 ${
                errors.task_name ? 'border-rose-500 ring-2 ring-rose-500/20' : 'border-slate-200 focus:ring-slate-900/10 focus:border-slate-900'
              }`}
            />
            {errors.task_name && <p className="text-[11px] font-semibold text-rose-500 mt-1">Work title is required</p>}
          </div>

          {/* 2. Description */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Description</label>
            <div className="border border-slate-200 rounded-xl overflow-hidden focus-within:border-slate-900 focus-within:ring-2 focus-within:ring-slate-900/10 transition-all">
              <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center gap-4 text-slate-500">
                <button type="button" className="hover:text-slate-900"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path></svg></button>
                <button type="button" className="hover:text-slate-900"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4"></line><line x1="14" y1="20" x2="5" y2="20"></line><line x1="15" y1="4" x2="9" y2="20"></line></svg></button>
                <button type="button" className="hover:text-slate-900"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4v6a6 6 0 0 0 12 0V4"></path><line x1="4" y1="20" x2="20" y2="20"></line><line x1="12" y1="10" x2="12" y2="20"></line></svg></button>
                <div className="w-px h-4 bg-slate-200"></div>
                <button type="button" className="hover:text-slate-900"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg></button>
                <button type="button" className="hover:text-slate-900"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="4" y2="6"></line><line x1="3" y1="12" x2="4" y2="12"></line><line x1="3" y1="18" x2="4" y2="18"></line></svg></button>
                <div className="w-px h-4 bg-slate-200"></div>
                <button type="button" className="hover:text-slate-900"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg></button>
                <button type="button" className="hover:text-slate-900"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></button>
              </div>
              <textarea 
                name="task_description"
                placeholder="Add optional notes, instructions or acceptance criteria..."
                value={formData.task_description}
                onChange={handleChange}
                className="w-full p-4 min-h-[110px] resize-none focus:outline-none text-sm text-slate-900 bg-white"
              ></textarea>
            </div>
          </div>

          {/* 3. Work Type & Project / team */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Work type <span className="text-rose-500">*</span></label>
              <Select
                name="task_kpi_type_id"
                options={displayTypes.map(t => ({ value: t._id, label: `${t.name} (${t.score !== undefined ? t.score : 0} pts)` }))}
                className="text-sm text-slate-900"
                classNamePrefix="select"
                placeholder="Select work type..."
                onChange={opt => handleChange({ target: { name: 'task_kpi_type_id', value: opt ? opt.value : '' } })}
                value={displayTypes.filter(t => t._id === formData.task_kpi_type_id).map(t => ({ value: t._id, label: `${t.name} (${t.score !== undefined ? t.score : 0} pts)` }))[0] || null}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: errors.task_kpi_type_id ? '#f43f5e' : '#e2e8f0',
                    boxShadow: errors.task_kpi_type_id ? '0 0 0 2px rgba(244, 63, 94, 0.2)' : 'none',
                    borderRadius: '0.75rem',
                    padding: '2px',
                    '&:hover': {
                      borderColor: errors.task_kpi_type_id ? '#f43f5e' : '#0f172a'
                    }
                  })
                }}
              />
              {errors.task_kpi_type_id && <p className="text-[11px] font-semibold text-rose-500 mt-1">Please select a work type</p>}
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Project / team <span className="text-rose-500">*</span></label>
              <Select
                name="project_id"
                options={projectsList.map(p => ({ value: p.project_id || p._id || p.id, label: p.name || p.project_name }))}
                className="text-sm text-slate-900"
                classNamePrefix="select"
                placeholder="Select project..."
                onChange={opt => handleChange({ target: { name: 'project_id', value: opt ? opt.value : '' } })}
                value={projectsList.filter(p => (p.project_id || p._id || p.id) === formData.project_id).map(p => ({ value: p.project_id || p._id || p.id, label: p.name || p.project_name }))[0] || null}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: errors.project_id ? '#f43f5e' : '#e2e8f0',
                    boxShadow: errors.project_id ? '0 0 0 2px rgba(244, 63, 94, 0.2)' : 'none',
                    borderRadius: '0.75rem',
                    padding: '2px',
                    '&:hover': {
                      borderColor: errors.project_id ? '#f43f5e' : '#0f172a'
                    }
                  })
                }}
              />
              {errors.project_id && <p className="text-[11px] font-semibold text-rose-500 mt-1">Please select a project</p>}
            </div>
          </div>

          {/* 4. Assignees */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Assignees <span className="text-rose-500">*</span></label>
            <Select
              name="assigned_to"
              options={(eligibleUsers || []).map(u => ({ value: u._id || u.id, label: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || u.email }))}
              className="text-sm text-slate-900"
              classNamePrefix="select"
              placeholder={formData.project_id ? "Select assignee..." : "Select project first..."}
              isDisabled={!formData.project_id}
              onChange={handleAssigneeChange}
              value={(eligibleUsers || [])
                .filter(u => formData.assigned_to.includes(u._id || u.id))
                .map(u => ({ value: u._id || u.id, label: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || u.email }))[0] || null}
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: errors.assigned_to ? '#f43f5e' : '#e2e8f0',
                  boxShadow: errors.assigned_to ? '0 0 0 2px rgba(244, 63, 94, 0.2)' : 'none',
                  borderRadius: '0.75rem',
                  padding: '2px',
                  '&:hover': {
                    borderColor: errors.assigned_to ? '#f43f5e' : '#0f172a'
                  }
                })
              }}
            />
            {errors.assigned_to && <p className="text-[11px] font-semibold text-rose-500 mt-1">Please select an assignee</p>}
          </div>

          {/* 5. Recurring Schedule Box */}
          <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50 transition-all">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <div className="text-sm font-bold text-slate-900">Repeat this work</div>
                  {isRecurring && (
                    <span className="px-2 py-0.5 rounded-full bg-slate-900 text-white text-[10px] font-bold tracking-wide flex items-center gap-1 shadow-2xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                      {recurrenceFrequency === 'WEEKLY' ? 'Every week' : 'Every month'}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {isRecurring 
                    ? `Automatically repeats on a ${recurrenceFrequency.toLowerCase()} schedule. Due date is not required.` 
                    : "Automatically repeat on a weekly or monthly schedule"}
                </div>
              </div>

              <div className="flex p-1 bg-white border border-slate-300/80 rounded-xl shrink-0 shadow-2xs">
                <button
                  type="button"
                  onClick={() => {
                    if (isRecurring && recurrenceFrequency === "WEEKLY") {
                      setIsRecurring(false);
                    } else {
                      setIsRecurring(true);
                      setRecurrenceFrequency("WEEKLY");
                    }
                  }}
                  className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition-all ${
                    isRecurring && recurrenceFrequency === "WEEKLY"
                      ? "bg-slate-900 text-white shadow-xs"
                      : "text-slate-700 hover:text-slate-900 hover:bg-slate-100/60 font-semibold"
                  }`}
                >
                  Weekly
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (isRecurring && recurrenceFrequency === "MONTHLY") {
                      setIsRecurring(false);
                    } else {
                      setIsRecurring(true);
                      setRecurrenceFrequency("MONTHLY");
                    }
                  }}
                  className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition-all ${
                    isRecurring && recurrenceFrequency === "MONTHLY"
                      ? "bg-slate-900 text-white shadow-xs"
                      : "text-slate-700 hover:text-slate-900 hover:bg-slate-100/60 font-semibold"
                  }`}
                >
                  Monthly
                </button>
              </div>
            </div>
          </div>

          {/* 6. Priority, Due date, and KPI points */}
          <div className={`grid grid-cols-1 ${isTypeRecurring ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-4 items-start`}>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Priority</label>
              <div className="flex p-1 bg-slate-100 border border-slate-200/80 rounded-xl">
                <button 
                  type="button"
                  onClick={() => handlePriority("Low")}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${formData.task_priority === "Low" ? "bg-slate-900 text-white shadow-2xs" : "text-slate-600 hover:text-slate-900 font-semibold"}`}
                >Low</button>
                <button 
                  type="button"
                  onClick={() => handlePriority("Medium")}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${formData.task_priority === "Medium" ? "bg-slate-900 text-white shadow-2xs" : "text-slate-600 hover:text-slate-900 font-semibold"}`}
                >Med</button>
                <button 
                  type="button"
                  onClick={() => handlePriority("High")}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${formData.task_priority === "High" ? "bg-slate-900 text-white shadow-2xs" : "text-slate-600 hover:text-slate-900 font-semibold"}`}
                >High</button>
              </div>
            </div>

            {!isTypeRecurring && (
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Due date <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  </div>
                  <input 
                    type="date" 
                    name="task_due_date"
                    min={todayStr}
                    value={formData.task_due_date}
                    onChange={handleChange}
                    className={`w-full pl-9 pr-3 py-2 border rounded-xl focus:outline-none focus:ring-2 transition-all text-sm text-slate-900 ${
                      errors.task_due_date || errors.due_past ? 'border-rose-500 ring-2 ring-rose-500/20' : 'border-slate-200 focus:ring-slate-900/10 focus:border-slate-900'
                    }`}
                  />
                </div>
                {errors.task_due_date && <p className="text-[11px] font-semibold text-rose-500 mt-1">Due date is required</p>}
                {errors.due_past && <p className="text-[11px] font-semibold text-rose-500 mt-1">Due date cannot be in the past</p>}
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">KPI points</label>
              <input 
                type="number" 
                name="assigned_kpi_points"
                readOnly
                disabled
                value={formData.assigned_kpi_points}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-200 bg-slate-100/80 rounded-xl text-sm text-slate-900 text-center font-bold cursor-not-allowed select-none focus:outline-none"
              />
            </div>
          </div>

          {/* 7. Bottom Submit Bar */}
          <div className="pt-4 border-t border-slate-200/80 flex items-center justify-end gap-3">
            <Link
              to="/kpi/works"
              className="px-5 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs !no-underline"
            >
              Cancel
            </Link>
            <button 
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition-all shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {loading ? "Creating..." : "Create Work"}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-80 shrink-0 space-y-6">
                    {/* Live Preview */}
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
            <div className="px-5 py-4 sm:px-6 sm:py-4 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-900 mb-0.5">Live preview</h2>
            </div>

            <div className="p-5 sm:p-6">
              <div className="bg-[#f7f6f3] rounded-xl border border-slate-200/80 p-4 relative transition-shadow">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 rounded bg-white text-slate-700 text-[10px] font-bold tracking-wide shadow-2xs">
                    {selectedType?.name || "Type"}
                  </span>
                  <span className="text-[10px] text-slate-500 font-medium">
                    {projectsList.find(p => p.project_id === formData.project_id)?.name || "No project"}
                  </span>
                  {isRecurring && (
                    <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-bold tracking-wide">
                      🔄 {recurrenceFrequency}
                    </span>
                  )}
                </div>
                <h3 className="text-sm font-bold text-slate-900 leading-snug mb-3 pr-8">
                  {formData.task_name || "New work"}
                </h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 font-medium">
                    <div className="flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                      {formData.task_due_date ? new Date(formData.task_due_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : "No date"}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${formData.task_priority === "High" ? "bg-rose-500" : formData.task_priority === "Low" ? "bg-slate-400" : "bg-amber-500"}`}></span>
                      {formData.task_priority}
                    </div>
                  </div>
                </div>
                
                <div className="absolute right-4 bottom-4 bg-slate-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-2xs">
                  +{formData.assigned_kpi_points || 0}
                </div>
              </div>
            </div>
          </div>

          {/* KPI Ladder */}
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
            <div className="px-5 py-4 sm:px-6 sm:py-4 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-900 mb-0.5">KPI ladder</h2>
            </div>

            <div className="p-5 sm:p-6">
              <div className="space-y-3">
                {displayTypes.length === 0 ? (
                  <div className="py-4 text-center text-sm text-slate-500">
                    No task types configured.
                  </div>
                ) : (
                  displayTypes.map((item, i) => (
                    <div key={item._id || i} className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${['bg-slate-400', 'bg-indigo-500', 'bg-emerald-500', 'bg-blue-500', 'bg-amber-500', 'bg-purple-500'][i % 6]}`}></div>
                        <span className="text-slate-600">{item.name}</span>
                      </div>
                      <span className="font-mono font-semibold text-slate-900 text-[11px]">{item.score !== undefined ? `${item.score} pts` : "—"}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default KPICreateWork;
